import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, ArrowLeft, Pencil, Trash2, QrCode, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ZoneDashboard = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      const response = await axios.get(`${API}/zones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZones(response.data);
    } catch (error) {
      toast.error('Failed to load zones');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async (zoneId, currentStatus) => {
    try {
      await axios.patch(
        `${API}/zones/${zoneId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(currentStatus ? 'Zone unpublished' : 'Zone published');
      fetchZones();
    } catch (error) {
      toast.error('Failed to update publish status');
    }
  };

  const handleDelete = async (zoneId) => {
    try {
      await axios.delete(`${API}/zones/${zoneId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Zone deleted');
      fetchZones();
    } catch (error) {
      toast.error('Failed to delete zone');
    }
  };

  const handleGenerateQR = async (zoneId) => {
    try {
      await axios.post(
        `${API}/zones/${zoneId}/generate-qr`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('QR code generated');
      fetchZones();
    } catch (error) {
      toast.error('Failed to generate QR code');
    }
  };

  const filteredZones = zones.filter(zone => 
    zone.title_tc.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (zone.title_en || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background" data-testid="zone-dashboard-page">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/staff/dashboard')}
              variant="ghost"
              size="icon"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-heading font-bold">Zone Management</h1>
              <p className="text-sm text-muted-foreground">Organize exhibits into zones</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Input
            placeholder="Search zones..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-xs"
            data-testid="search-input"
          />
          <Button
            onClick={() => navigate('/staff/zone/create')}
            data-testid="create-zone-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Zone
          </Button>
        </div>

        {/* Zones Table */}
        {loading ? (
          <div className="text-center py-12" data-testid="loading-state">Loading...</div>
        ) : filteredZones.length === 0 ? (
          <div className="text-center py-12 border border-border rounded-lg" data-testid="empty-state">
            <MapPin className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No zones found</p>
            <Button onClick={() => navigate('/staff/zone/create')}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Zone
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="zones-table">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-medium">Code</th>
                    <th className="text-left px-6 py-3 text-sm font-medium">Image</th>
                    <th className="text-left px-6 py-3 text-sm font-medium">Title (TC)</th>
                    <th className="text-left px-6 py-3 text-sm font-medium">Exhibits</th>
                    <th className="text-left px-6 py-3 text-sm font-medium">Status</th>
                    <th className="text-left px-6 py-3 text-sm font-medium">QR Code</th>
                    <th className="text-left px-6 py-3 text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredZones.map((zone) => (
                    <tr key={zone.id} data-testid={`zone-row-${zone.id}`}>
                      <td className="px-6 py-4">
                        <div className="font-mono text-2xl font-bold text-primary">{zone.code || '---'}</div>
                      </td>
                      <td className="px-6 py-4">
                        {zone.image_url ? (
                          <img
                            src={`${API}${zone.image_url}?t=${zone.updated_at ? new Date(zone.updated_at).getTime() : Date.now()}`}
                            alt={zone.title_tc}
                            className="w-16 h-16 object-cover rounded"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                            <MapPin className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium">{zone.title_tc}</div>
                        <div className="text-sm text-muted-foreground">{zone.title_en}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {zone.exhibit_count || 0} exhibits
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleTogglePublish(zone.id, zone.is_published)}
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            zone.is_published
                              ? 'bg-green-100 text-green-700'
                              : 'bg-zinc-100 text-zinc-700'
                          }`}
                          data-testid={`publish-toggle-${zone.id}`}
                        >
                          {zone.is_published ? 'Published' : 'Unpublished'}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        {zone.qr_code_url ? (
                          <span className="text-green-600 text-sm" data-testid={`qr-status-${zone.id}`}>Generated</span>
                        ) : (
                          <Button
                            onClick={() => handleGenerateQR(zone.id)}
                            variant="outline"
                            size="sm"
                            data-testid={`generate-qr-${zone.id}`}
                          >
                            <QrCode className="w-4 h-4 mr-1" />
                            Generate
                          </Button>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => navigate(`/staff/zone/${zone.id}/edit`)}
                            variant="ghost"
                            size="sm"
                            data-testid={`edit-button-${zone.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                data-testid={`delete-button-${zone.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Zone</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure? This will remove the zone but not the linked exhibits.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(zone.id)}>
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZoneDashboard;
